'use client';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useInView, useMotionValue, animate, useReducedMotion } from 'framer-motion';
import { useLocale } from 'next-intl';
import { useHydrated } from '@/lib/use-hydrated';

interface CountUpProps {
  value: number;
  className?: string;
}

export function CountUp({ value, className }: CountUpProps) {
  const reduced = useReducedMotion();
  const hydrated = useHydrated();
  const locale = useLocale();
  const ref = useRef<HTMLSpanElement>(null);
  const motionValue = useMotionValue(0);
  // null — анимация ещё не выдала ни одного кадра, показываем итоговое число.
  const [animated, setAnimated] = useState<number | null>(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const skipRef = useRef(false);

  // Если секция уже во вьюпорте на момент гидратации (переход по #numbers,
  // восстановление скролла при reload), считать не от чего — иначе итоговое
  // число из серверной разметки просядет до 0 и досчитается заново на глазах.
  useLayoutEffect(() => {
    const rect = ref.current?.getBoundingClientRect();
    skipRef.current = !!rect && rect.top < window.innerHeight && rect.bottom > 0;
  }, []);

  useEffect(() => {
    if (reduced || !inView || skipRef.current) return;
    const controls = animate(motionValue, value, {
      duration: 1.5,
      ease: 'easeOut',
      onUpdate: (v) => setAnimated(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, reduced, motionValue, value]);

  // В серверной разметке стоит итоговое число: краулеры и AI-движки без JS
  // читают реальную цифру, а не ноль. После гидратации, если анимация вообще
  // будет (не skip), сразу показываем 0 — иначе margin: '-80px' у useInView даёт
  // окно, где элемент уже на экране, а inView ещё не сработал: было бы видно,
  // как настоящее число мигает в 0 при старте отсчёта.
  const display = reduced || !hydrated || skipRef.current ? value : (animated ?? 0);

  return (
    <span ref={ref} className={className}>
      {display.toLocaleString(locale)}
    </span>
  );
}
